import { type SaleDocumentSummary } from "../types";

/** 名称が無い物件の表示名。 */
export const NO_BUILDING_NAME = "名称なし";

/**
 * 物件の表示名（マンション名。無ければ住所）。
 * @param document - 物件
 * @returns 表示名
 */
export const displayName = (
  document: Pick<SaleDocumentSummary, "buildingName" | "address">
): string =>
  document.buildingName !== ""
    ? document.buildingName
    : document.address !== ""
    ? document.address
    : NO_BUILDING_NAME;

/**
 * 見出しの下に住所の行を出すか。見出しが住所と同じ（マンション名が無い物件）なら出さない。
 * @param document - 物件
 * @returns 住所の行を出すなら true
 */
export const showsAddressLine = (
  document: Pick<SaleDocumentSummary, "buildingName" | "address">
): boolean =>
  document.address !== "" && displayName(document) !== document.address;

/**
 * 物件を見分けるための補足（建物の種類・階数・構造・用途）。値があるものだけを「・」でつなぐ。
 * 階数は API の文字（例「地上5階地下1階」）をそのまま使う。
 * @param document - 物件
 * @param keys - 出す項目と順序。既定は4項目すべて
 * @returns 例「一棟建物・地上5階・鉄筋コンクリート造・共同住宅」。値が全く無ければ ""
 */
export const formatBuildingFeatures = (
  document: Pick<
    SaleDocumentSummary,
    "buildingType" | "floors" | "structure" | "use"
  >,
  keys: ReadonlyArray<"buildingType" | "floors" | "structure" | "use"> = [
    "buildingType",
    "floors",
    "structure",
    "use",
  ]
): string =>
  keys
    .map((key) => document[key].trim())
    .filter((value) => value !== "")
    .join("・");

/**
 * 価格（クレジット）の表示。未設定は購入できない旨を返す。
 * @param price - 1PDFあたりの価格。null は未設定
 * @returns 表示用の文字列
 */
export const formatPrice = (price: number | null): string =>
  price === null
    ? "現在購入できません"
    : `${price.toLocaleString("ja-JP")}クレジット`;

/**
 * 購入状況の表示。
 * @param document - 物件
 * @returns 購入状況の文字列
 */
export const purchaseStatusLabel = (
  document: Pick<SaleDocumentSummary, "fileCount" | "purchasedCount">
): string => {
  if (document.fileCount === 0) return "購入できる資料なし";
  if (document.purchasedCount >= document.fileCount) return "購入済み";
  if (document.purchasedCount > 0) {
    return `一部購入済み（${document.purchasedCount}/${document.fileCount}）`;
  }
  return `${document.fileCount}件の資料`;
};

/**
 * 価格に円を併記した表示（1クレジット=1円）。未設定は購入できない旨を返す。
 * @param price - 1PDFあたりの価格。null は未設定
 * @returns 例「500クレジット（500円・税別）」
 */
export const formatPriceWithYen = (price: number | null): string =>
  price === null
    ? formatPrice(null)
    : `${formatPrice(price)}（${price.toLocaleString("ja-JP")}円・税別）`;

/**
 * 資料の種別と頁数。種別が空なら「資料」、頁数が null なら頁数は出さない。
 * @param file - PDF
 * @param file.documentType - 資料種別
 * @param file.pageCount - 頁数
 * @returns 例「建築計画概要書、12ページ」
 */
export const formatFileLabel = (file: {
  documentType: string;
  pageCount: number | null;
}): string => {
  const type = file.documentType || "資料";
  return file.pageCount === null ? type : `${type}、${file.pageCount}ページ`;
};

/** 購入状況の種類。 */
export type PurchaseState = "unpurchased" | "partial" | "purchased" | "none";

/**
 * 件数から購入状況の種類を決める。
 * @param document - 物件
 * @returns 種類
 */
export const purchaseState = (
  document: Pick<SaleDocumentSummary, "fileCount" | "purchasedCount">
): PurchaseState => {
  if (document.fileCount === 0) return "none";
  if (document.purchasedCount >= document.fileCount) return "purchased";
  if (document.purchasedCount > 0) return "partial";
  return "unpurchased";
};
